import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { adminApi, apiError } from "@/lib/api";
import { ADMIN } from "@/constants/testIds";
import { fmtDate, fmtNum, StatusPill, Panel, PageHeader, Th, Td, Empty } from "@/components/admin/parts";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { ConfirmDialog } from "@/components/admin/ConfirmDialog";

export default function Users() {
  const [data, setData] = useState(null);
  const [q, setQ] = useState("");
  const [selected, setSelected] = useState(null);
  const [detail, setDetail] = useState(null);
  const [amount, setAmount] = useState("");
  const [reason, setReason] = useState("");
  const [actionReason, setActionReason] = useState("");
  const [log, setLog] = useState([]);
  const [confirm, setConfirm] = useState(null); // {title, description, danger, requireReason, run}
  const [loading, setLoading] = useState(false);

  const load = useCallback(() => {
    adminApi.users({ q: q || undefined, limit: 100 }).then(setData).catch(() => {});
  }, [q]);
  useEffect(() => { load(); }, [load]);

  const openUser = async (u) => {
    setSelected(u); setDetail(null); setAmount(""); setReason(""); setLog([]);
    try {
      setDetail(await adminApi.user(u.id));
      adminApi.userActionLog(u.id).then((r) => setLog(r.items || [])).catch(() => {});
    } catch (e) { toast.error("Nie udało się wczytać konta."); }
  };

  const refreshDetail = async () => {
    setDetail(await adminApi.user(selected.id));
    adminApi.userActionLog(selected.id).then((r) => setLog(r.items || [])).catch(() => {});
    load();
  };

  const runConfirm = async () => {
    if (!confirm) return;
    setLoading(true);
    try { await confirm.run(actionReason); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setLoading(false); setConfirm(null); setActionReason(""); }
  };

  const toggleBlock = () => {
    const next = detail.user.status === "BLOCKED" ? "ACTIVE" : "BLOCKED";
    setActionReason("");
    setConfirm({
      title: next === "BLOCKED" ? "Zablokuj konto" : "Odblokuj konto",
      description: `Konto ${detail.user.email} zostanie ${next === "BLOCKED" ? "zablokowane" : "odblokowane"}.`,
      danger: next === "BLOCKED",
      requireReason: next === "BLOCKED",
      run: async (r) => { await adminApi.updateUser(selected.id, { status: next, reason: r }); toast.success("Zaktualizowano status."); await refreshDetail(); },
    });
  };
  const softDelete = () => {
    setActionReason("");
    setConfirm({
      title: "Usuń konto (bez trwałego kasowania)",
      description: `Konto ${detail.user.email} zostanie oznaczone jako usunięte i wylogowane ze wszystkich sesji. Dane pozostają w bazie — konto możesz później przywrócić.`,
      danger: true,
      requireReason: true,
      run: async (r) => { await adminApi.updateUser(selected.id, { status: "DELETED", reason: r }); toast.success("Konto zostało usunięte (można je przywrócić)."); await refreshDetail(); },
    });
  };
  const restore = () => {
    setActionReason("");
    setConfirm({
      title: "Przywróć konto",
      description: `Konto ${detail.user.email} znów będzie aktywne i użytkownik będzie mógł się zalogować.`,
      danger: false,
      run: async (r) => { await adminApi.updateUser(selected.id, { status: "ACTIVE", reason: r }); toast.success("Konto przywrócone."); await refreshDetail(); },
    });
  };
  const toggleRole = () => {
    const next = detail.user.role === "admin" ? "user" : "admin";
    setActionReason("");
    setConfirm({
      title: next === "admin" ? "Nadaj rolę administratora" : "Odbierz rolę administratora",
      description: `Rola konta ${detail.user.email} zmieni się na „${next}".`,
      danger: next === "user",
      run: async () => { await adminApi.updateUser(selected.id, { role: next }); toast.success("Zaktualizowano rolę."); await refreshDetail(); },
    });
  };
  const adjust = () => {
    const amt = parseInt(amount, 10);
    if (!amt || !reason.trim()) { toast.error("Podaj kwotę (≠0) i powód."); return; }
    setConfirm({
      title: "Potwierdź korektę salda",
      description: `Zmienisz saldo konta ${detail.user.email} o ${amt} kredytów.`,
      run: async () => {
        await adminApi.adjustCredits(selected.id, { amount: amt, reason: reason.trim() });
        toast.success("Korekta zapisana."); setAmount(""); setReason(""); await refreshDetail();
      },
    });
  };

  return (
    <div>
      <PageHeader title="Użytkownicy" subtitle="Konta, salda i korekty kredytów" />
      <div className="mb-4 flex">
        <input data-testid={ADMIN.userSearch} value={q} onChange={(e) => setQ(e.target.value)} placeholder="Szukaj po adresie e-mail…" className="h-10 w-full max-w-md rounded-field border border-brand-line bg-brand-surface px-3 text-sm text-brand-text outline-none focus:border-brand-accent/60" />
      </div>

      <Panel testId={ADMIN.usersTable}>
        {!data ? <Empty>Ładowanie…</Empty> : data.items.length === 0 ? <Empty>Brak kont.</Empty> : (
          <div className="overflow-x-auto">
            <table className="w-full min-w-[720px]">
              <thead><tr className="border-b border-brand-line">
                <Th>E-mail</Th><Th>Rola</Th><Th>Status</Th><Th>Weryfikacja</Th><Th className="text-right">Saldo</Th><Th>Dołączył</Th>
              </tr></thead>
              <tbody>
                {data.items.map((u) => (
                  <tr key={u.id} data-testid={ADMIN.userRow} onClick={() => openUser(u)} className="cursor-pointer border-b border-brand-line/60 hover:bg-brand-elevated/40">
                    <Td><div className="font-medium text-brand-text">{u.email}</div>{u.first_name && <div className="text-xs text-brand-muted">{u.first_name}</div>}</Td>
                    <Td><StatusPill status={u.role === "admin" ? "settled" : "ZAMKNIETA"} label={u.role} /></Td>
                    <Td><StatusPill status={u.status} /></Td>
                    <Td className="text-brand-sec">{u.email_verified ? "✓" : "—"}</Td>
                    <Td className="text-right font-semibold text-brand-accent">{fmtNum(u.balance)}</Td>
                    <Td className="whitespace-nowrap text-brand-sec">{fmtDate(u.created_at)}</Td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Panel>

      <Dialog open={!!selected} onOpenChange={(o) => !o && setSelected(null)}>
        <DialogContent className="max-w-2xl" data-testid={ADMIN.userDetail}>
          <DialogHeader><DialogTitle>{selected?.email}</DialogTitle></DialogHeader>
          <DialogDescription className="sr-only">Szczegóły konta: status, rola, saldo i akcje administracyjne.</DialogDescription>
          {!detail ? <Empty>Ładowanie…</Empty> : (
            <div className="space-y-5">
              <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
                <Stat label="Saldo" value={fmtNum(detail.wallet.balance)} />
                <Stat label="Kupione" value={fmtNum(detail.wallet.total_purchased)} />
                <Stat label="Bonus" value={fmtNum(detail.wallet.total_bonus)} />
                <Stat label="Wydane" value={fmtNum(detail.wallet.total_spent)} />
              </div>
              <div className="flex flex-wrap gap-2">
                {detail.user.status === "DELETED" ? (
                  <button data-testid={ADMIN.userRestoreButton} onClick={restore} className="rounded-field border border-emerald-500/40 px-3 py-2 text-sm font-semibold text-emerald-300 hover:border-emerald-400">
                    Przywróć konto
                  </button>
                ) : (
                  <>
                    <button data-testid={ADMIN.userBlockButton} onClick={toggleBlock} className="rounded-field border border-brand-line px-3 py-2 text-sm text-brand-text hover:border-brand-accent/50">
                      {detail.user.status === "BLOCKED" ? "Odblokuj konto" : "Zablokuj konto"}
                    </button>
                    <button data-testid={ADMIN.userDeleteButton} onClick={softDelete} className="rounded-field border border-red-500/40 px-3 py-2 text-sm font-semibold text-red-300 hover:border-red-400">
                      Usuń konto
                    </button>
                  </>
                )}
                <button data-testid={ADMIN.userRoleButton} onClick={toggleRole} className="rounded-field border border-brand-line px-3 py-2 text-sm text-brand-text hover:border-brand-accent/50">
                  {detail.user.role === "admin" ? "Odbierz rolę admina" : "Nadaj rolę admina"}
                </button>
              </div>

              <div className="rounded-card border border-brand-line bg-brand-bg/50 p-4">
                <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-brand-muted">Korekta kredytów</p>
                <div className="flex flex-wrap gap-2">
                  <input data-testid={ADMIN.creditAdjustAmount} type="number" value={amount} onChange={(e) => setAmount(e.target.value)} placeholder="np. +10 / -5" className="h-10 w-28 rounded-field border border-brand-line bg-brand-surface px-3 text-sm text-brand-text outline-none" />
                  <input data-testid={ADMIN.creditAdjustReason} value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Powód korekty" className="h-10 flex-1 rounded-field border border-brand-line bg-brand-surface px-3 text-sm text-brand-text outline-none" />
                  <button data-testid={ADMIN.creditAdjustSubmit} onClick={adjust} className="h-10 rounded-field bg-brand-accent px-4 text-sm font-semibold text-brand-on">Zastosuj</button>
                </div>
              </div>

              <div data-testid="admin-user-action-log">
                <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-brand-muted">Historia działań administracyjnych (tylko do odczytu)</p>
                <div className="max-h-56 overflow-y-auto rounded-card border border-brand-line">
                  {log.length === 0 ? (
                    <p className="p-3 text-sm text-brand-muted">Brak działań administracyjnych na tym koncie.</p>
                  ) : (
                    <table className="w-full text-sm">
                      <tbody>
                        {log.map((a) => (
                          <tr key={a.id} className="border-b border-brand-line/60 align-top">
                            <Td className="whitespace-nowrap text-brand-sec">{fmtDate(a.created_at)}</Td>
                            <Td>
                              <span className={`inline-block rounded-full px-2 py-0.5 text-[11px] font-semibold ${a.action === "DELETE" ? "bg-red-500/20 text-red-200" : a.action === "BLOCK" ? "bg-red-500/15 text-red-300" : "bg-emerald-500/15 text-emerald-300"}`}>{a.action_label}</span>
                              <span className="ml-2 text-xs text-brand-muted">{a.status_before} → {a.status_after}</span>
                              {a.reason && <div className="mt-0.5 text-xs text-brand-sec">Powód: {a.reason}</div>}
                            </Td>
                            <Td className="whitespace-nowrap text-xs text-brand-muted">{a.admin_email}</Td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                </div>
              </div>

              <div>
                <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-brand-muted">Historia kredytów (ostatnie)</p>
                <div className="max-h-48 overflow-y-auto rounded-card border border-brand-line">
                  <table className="w-full text-sm">
                    <tbody>
                      {detail.ledger.length === 0 ? <tr><td className="p-3 text-brand-muted">Brak zdarzeń.</td></tr> : detail.ledger.map((l) => (
                        <tr key={l.id} className="border-b border-brand-line/60">
                          <Td className="whitespace-nowrap text-brand-sec">{fmtDate(l.created_at)}</Td>
                          <Td>{l.description}</Td>
                          <Td className={`text-right font-semibold ${l.amount >= 0 ? "text-brand-accent" : "text-brand-sec"}`}>{l.amount > 0 ? "+" : ""}{l.amount}</Td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <ConfirmDialog
        open={!!confirm}
        onOpenChange={(o) => !o && setConfirm(null)}
        title={confirm?.title}
        description={confirm?.description}
        danger={confirm?.danger}
        confirmLabel="Potwierdź"
        onConfirm={runConfirm}
        loading={loading}
        requireReason={confirm?.requireReason}
        reasonValue={actionReason}
        onReasonChange={setActionReason}
        reasonLabel="Powód (wymagany)"
        testId="admin-user-confirm"
      />
    </div>
  );
}

const Stat = ({ label, value }) => (
  <div className="rounded-card border border-brand-line bg-brand-bg/50 p-3">
    <p className="font-display text-lg font-bold text-brand-text">{value}</p>
    <p className="text-xs text-brand-muted">{label}</p>
  </div>
);
